import {
  MIDDLEWARE_DELIVERY_PATHS,
  DOT_FOLIO_PATHS,
  OUTBOUND_ADMIN_PATHS,
  PICKING_SIMULATION_API_PATHS,
} from '@amoble/contracts';
import {
  middlewareDeliveryOverviewSchema,
  dotFolioOverviewSchema,
  dotFolioPolicySchema,
  dotFolioRequestSchema,
  outboundSubscriptionViewSchema,
  outboundDeliveryPageSchema,
  pickingSimulationOrderListSchema,
  simulatedPickingNoticeResponseSchema,
  simulationCapabilitiesSchema,
} from './contract-schemas';
/**
 * Implementacion HTTP de `MonitorApi`.
 *
 * Reglas que sigue este cliente:
 *
 * - **Zod en el borde, tambien de entrada**: cada respuesta 2xx se valida contra
 *   su schema antes de devolverse. Si la API cambia y la UI no, la pantalla dice
 *   exactamente que campo no calza en vez de renderizar `undefined`.
 * - **Nada muere en silencio**: cada falla sale tipada (`ApiError`,
 *   `UnauthorizedError`, `ContractValidationError`, `NetworkError`) con su
 *   mensaje en espanol.
 * - **El token no se guarda aca**: se lee del `token-store` en cada request, asi
 *   el cliente se construye una sola vez al arrancar la app.
 *
 * `API_ROUTES` se deriva de `MONITORING_API_PATHS` (`@amoble/contracts`), que es
 * la MISMA tabla contra la que `apps/api` verifica su documento OpenAPI. Antes
 * este archivo tenia su propia lista con un prefijo `/monitoring/...` que la API
 * nunca publico: toda la UI daba 404 y el CI no lo veia, porque los tests de
 * pantalla inyectan un `MonitorApi` falso. Si hay que cambiar una ruta, se
 * cambia en el contrato y las dos puntas se enteran.
 */
import { MONITORING_API_PATHS, WAREHOUSE_API_PATHS, monitoringApiPath } from '@amoble/contracts';
import { z } from 'zod';
import {
  ApiError,
  ContractValidationError,
  ForbiddenError,
  NetworkError,
  UnauthorizedError,
} from './errors';
import type { MonitorApi } from './monitor-api';
import {
  dashboardSummarySchema,
  flowGraphSchema,
  flowRunListResponseSchema,
  flowRunNeighborsSchema,
  warehouseDiscoveryOutcomeSchema,
  warehouseMappingSchema,
  warehouseProvisionOutcomeSchema,
} from './contract-schemas';
import {
  deadLetterListSchema,
  messageDetailSchema,
  messageListSchema,
  messageTraceSchema,
  problemDetailsSchema,
  quarantineListSchema,
  sessionUserSchema,
} from './monitor-schemas';
import type {
  DashboardPeriod,
  DashboardSummary,
  FlowGraph,
  FlowRunNeighbors,
  FlowRunSummary,
  MessageLogEntry,
  WarehouseDiscoveryOutcome,
  WarehouseLocationOverrides,
  WarehouseMapping,
  WarehouseMappingUpsert,
  WarehouseProvisionOutcome,
} from './contracts';
import type {
  DeadLetterRow,
  IncidentFilters,
  MessageDetail,
  MessageFilters,
  PageState,
  Paginated,
  QuarantineRow,
  RunFilters,
  SessionUser,
} from './types';
import { clearToken, getToken } from '../auth/token-store';
import { incidentFiltersToQuery, messageFiltersToQuery, runFiltersToQuery } from '../lib/filters';

/**
 * Rutas de la API de monitoreo, relativas a la base configurada.
 *
 * Todas salen de `MONITORING_API_PATHS`: no hay ni una cadena de ruta escrita a
 * mano en este archivo.
 */
export const API_ROUTES = {
  session: MONITORING_API_PATHS.session,
  messages: MONITORING_API_PATHS.messages,
  message: (id: string): string => monitoringApiPath(MONITORING_API_PATHS.message, { id }),
  trace: (correlationId: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.messageTrace, { correlationId }),
  flowGraph: (correlationId: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.flowGraph, { correlationId }),
  runs: MONITORING_API_PATHS.flowRuns,
  runNeighbors: (correlationId: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.flowRunNeighbors, { correlationId }),
  dashboard: MONITORING_API_PATHS.dashboard,
  deadLetters: MONITORING_API_PATHS.deadLetters,
  replayDeadLetter: (id: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.replayDeadLetter, { id }),
  discardDeadLetter: (id: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.discardDeadLetter, { id }),
  quarantine: MONITORING_API_PATHS.quarantine,
  resolveQuarantine: (id: string): string =>
    monitoringApiPath(MONITORING_API_PATHS.resolveQuarantine, { id }),
  warehouses: WAREHOUSE_API_PATHS.list,
  discoverWarehouses: WAREHOUSE_API_PATHS.discover,
  createWarehouseInJanis: (code: string): string =>
    monitoringApiPath(WAREHOUSE_API_PATHS.createInJanis, { code }),
} as const;

const warehouseListSchema = z.array(warehouseMappingSchema);

export interface HttpMonitorApiOptions {
  /** Base de la API. Por defecto `/api` (proxy de Vite en desarrollo). */
  readonly baseUrl?: string;
  /** De donde sale el ID token; por defecto del `token-store`. */
  readonly readToken?: () => string | null;
  /** Se llama ante un 401, antes de propagar el error. */
  readonly onUnauthorized?: () => void;
}

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH';
  readonly path: string;
  readonly query?: Record<string, string>;
  readonly body?: unknown;
}

/** Detalle de un error de la API, leido de forma tolerante. */
interface ProblemInfo {
  readonly detail: string | null;
  readonly correlationId: string | null;
}

const EMPTY_PROBLEM: ProblemInfo = { detail: null, correlationId: null };

/**
 * Lee el cuerpo de error. Un error mal formado no puede impedir que mostremos
 * que hubo un error: si el cuerpo no es el esperado se cae al status HTTP.
 */
async function readProblem(response: Response): Promise<ProblemInfo> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Cuerpo vacio o no-JSON (proxy caido, HTML de error del balanceador).
    return EMPTY_PROBLEM;
  }
  const parsed = problemDetailsSchema.safeParse(body);
  if (!parsed.success) {
    return EMPTY_PROBLEM;
  }
  const problem = parsed.data;
  const listed = Array.isArray(problem.message) ? problem.message.join(' · ') : problem.message;
  return {
    detail: problem.detail ?? listed ?? problem.title ?? problem.error ?? null,
    correlationId: problem.correlationId ?? null,
  };
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: Record<string, string> | undefined,
): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const search = query === undefined ? '' : new URLSearchParams(query).toString();
  return search === '' ? `${base}${path}` : `${base}${path}?${search}`;
}

/** Construye el cliente HTTP que consumen las pantallas. */
export function createHttpMonitorApi(options: HttpMonitorApiOptions = {}): MonitorApi {
  const baseUrl = options.baseUrl ?? '/api';
  const readToken = options.readToken ?? getToken;
  const onUnauthorized = options.onUnauthorized ?? clearToken;

  async function send(request: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = readToken();
    if (token !== null) {
      // El ID token de Google viaja tal cual; la API lo verifica contra Google.
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (request.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    const init: RequestInit =
      request.body === undefined
        ? { method: request.method, headers }
        : { method: request.method, headers, body: JSON.stringify(request.body) };
    try {
      return await fetch(buildUrl(baseUrl, request.path, request.query), init);
    } catch (error) {
      throw new NetworkError(request.path, error);
    }
  }

  async function failFor(request: RequestOptions, response: Response): Promise<never> {
    const problem = await readProblem(response);
    if (response.status === 401) {
      onUnauthorized();
      throw new UnauthorizedError(problem.detail);
    }
    if (response.status === 403) {
      throw new ForbiddenError(problem.detail);
    }
    throw new ApiError(
      response.status,
      `La API respondio ${response.status} en ${request.path}`,
      problem.detail,
      problem.correlationId,
    );
  }

  /** GET + validacion contra el schema del contrato. */
  async function query<T extends z.ZodTypeAny>(
    request: RequestOptions,
    schema: T,
  ): Promise<z.infer<T>> {
    const response = await send(request);
    if (!response.ok) {
      await failFor(request, response);
    }
    let body: unknown;
    try {
      body = await response.json();
    } catch (error) {
      throw new NetworkError(request.path, error);
    }
    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      throw new ContractValidationError(request.path, parsed.error.issues);
    }
    return parsed.data;
  }

  /** POST sin cuerpo de respuesta util (acciones de operacion). */
  async function command(request: RequestOptions): Promise<void> {
    const response = await send(request);
    if (!response.ok) {
      await failFor(request, response);
    }
  }

  return {
    async getSession(): Promise<SessionUser> {
      return query({ method: 'GET', path: API_ROUTES.session }, sessionUserSchema);
    },

    async listMessages(
      filters: MessageFilters,
      page: PageState,
    ): Promise<Paginated<MessageLogEntry>> {
      return query(
        {
          method: 'GET',
          path: API_ROUTES.messages,
          query: messageFiltersToQuery(filters, page),
        },
        messageListSchema,
      );
    },

    async getMessage(id: string): Promise<MessageDetail> {
      return query({ method: 'GET', path: API_ROUTES.message(id) }, messageDetailSchema);
    },

    async getDashboard(period: DashboardPeriod): Promise<DashboardSummary> {
      return query(
        { method: 'GET', path: API_ROUTES.dashboard, query: { period } },
        dashboardSummarySchema,
      );
    },

    async getFlowGraph(correlationId: string): Promise<FlowGraph> {
      return query({ method: 'GET', path: API_ROUTES.flowGraph(correlationId) }, flowGraphSchema);
    },

    async listRuns(filters: RunFilters, page: PageState): Promise<Paginated<FlowRunSummary>> {
      return query(
        { method: 'GET', path: API_ROUTES.runs, query: runFiltersToQuery(filters, page) },
        flowRunListResponseSchema,
      );
    },

    async getRunNeighbors(correlationId: string): Promise<FlowRunNeighbors> {
      return query(
        { method: 'GET', path: API_ROUTES.runNeighbors(correlationId) },
        flowRunNeighborsSchema,
      );
    },

    async getTrace(correlationId: string): Promise<readonly MessageLogEntry[]> {
      const trace = await query(
        { method: 'GET', path: API_ROUTES.trace(correlationId) },
        messageTraceSchema,
      );
      return trace.messages;
    },

    async listDeadLetters(
      filters: IncidentFilters,
      page: PageState,
    ): Promise<Paginated<DeadLetterRow>> {
      return query(
        {
          method: 'GET',
          path: API_ROUTES.deadLetters,
          query: incidentFiltersToQuery(filters, page),
        },
        deadLetterListSchema,
      );
    },

    async replayDeadLetter(id: string): Promise<void> {
      await command({ method: 'POST', path: API_ROUTES.replayDeadLetter(id), body: {} });
    },

    async discardDeadLetter(id: string, reason: string): Promise<void> {
      await command({
        method: 'POST',
        path: API_ROUTES.discardDeadLetter(id),
        body: { reason },
      });
    },

    async listQuarantine(
      filters: IncidentFilters,
      page: PageState,
    ): Promise<Paginated<QuarantineRow>> {
      return query(
        {
          method: 'GET',
          path: API_ROUTES.quarantine,
          query: incidentFiltersToQuery(filters, page),
        },
        quarantineListSchema,
      );
    },

    async resolveQuarantine(id: string, note: string): Promise<void> {
      await command({ method: 'POST', path: API_ROUTES.resolveQuarantine(id), body: { note } });
    },

    async getSapDeliveries() {
      return query(
        { method: 'GET', path: MIDDLEWARE_DELIVERY_PATHS.overview },
        middlewareDeliveryOverviewSchema,
      );
    },
    async getDotFolios() {
      return query({ method: 'GET', path: DOT_FOLIO_PATHS.overview }, dotFolioOverviewSchema);
    },
    async saveDotFolioPolicy(input) {
      return query(
        { method: 'PATCH', path: DOT_FOLIO_PATHS.policy, body: input },
        dotFolioPolicySchema,
      );
    },
    async requestDotFolioBlock(input) {
      return query(
        { method: 'POST', path: DOT_FOLIO_PATHS.requests, body: input },
        dotFolioRequestSchema,
      );
    },
    async listSubscriptions() {
      return query(
        { method: 'GET', path: OUTBOUND_ADMIN_PATHS.list },
        z.array(outboundSubscriptionViewSchema),
      );
    },
    async saveSubscription(id, input) {
      return query(
        {
          method: id === null ? 'POST' : 'PATCH',
          path:
            id === null
              ? OUTBOUND_ADMIN_PATHS.list
              : OUTBOUND_ADMIN_PATHS.update.replace('{id}', encodeURIComponent(id)),
          body: input,
        },
        outboundSubscriptionViewSchema,
      );
    },
    async setSubscriptionActive(id, active) {
      return query(
        {
          method: 'PATCH',
          path: OUTBOUND_ADMIN_PATHS.state.replace('{id}', encodeURIComponent(id)),
          body: { active },
        },
        outboundSubscriptionViewSchema,
      );
    },
    async listSubscriptionDeliveries(id, page) {
      return query(
        {
          method: 'GET',
          path: OUTBOUND_ADMIN_PATHS.deliveries.replace('{id}', encodeURIComponent(id)),
          query: { page: String(page) },
        },
        outboundDeliveryPageSchema,
      );
    },
    async listWarehouses(): Promise<readonly WarehouseMapping[]> {
      return query({ method: 'GET', path: API_ROUTES.warehouses }, warehouseListSchema);
    },

    async discoverWarehouses(): Promise<WarehouseDiscoveryOutcome> {
      return query(
        { method: 'POST', path: API_ROUTES.discoverWarehouses, body: {} },
        warehouseDiscoveryOutcomeSchema,
      );
    },

    async createWarehouseInJanis(
      sapWarehouseCode: string,
      location: WarehouseLocationOverrides,
    ): Promise<WarehouseProvisionOutcome> {
      return query(
        {
          method: 'POST',
          path: API_ROUTES.createWarehouseInJanis(sapWarehouseCode),
          body: location,
        },
        warehouseProvisionOutcomeSchema,
      );
    },

    async saveWarehouse(mapping: WarehouseMappingUpsert): Promise<WarehouseMapping> {
      return query(
        { method: 'POST', path: API_ROUTES.warehouses, body: mapping },
        warehouseMappingSchema,
      );
    },

    async getSimulationCapabilities() {
      return query(
        { method: 'GET', path: PICKING_SIMULATION_API_PATHS.capabilities },
        simulationCapabilitiesSchema,
      );
    },

    async listPickingSimulationOrders() {
      return query(
        { method: 'GET', path: PICKING_SIMULATION_API_PATHS.orders },
        pickingSimulationOrderListSchema,
      );
    },

    async injectPickingNotice(input) {
      return query(
        { method: 'POST', path: PICKING_SIMULATION_API_PATHS.notices, body: input },
        simulatedPickingNoticeResponseSchema,
      );
    },
  };
}
